'use client';

import { toast as sonnerToast } from 'sonner';

export interface ToastOptions {
  duration?: number;
  position?: 'top-left' | 'top-center' | 'top-right' | 'bottom-left' | 'bottom-center' | 'bottom-right';
  dismissible?: boolean;
  action?: {
    label: string;
    onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
  };
  cancel?: {
    label: string;
    onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
  };
}

export interface LoadingToastOptions extends Omit<ToastOptions, 'duration'> {
  loadingText?: string;
  successText?: string;
  errorText?: string;
}

class ToastManager {
  // 基础通知方法
  success(message: string, options: ToastOptions = {}) {
    return sonnerToast.success(message, {
      duration: options.duration || 4000,
      position: options.position,
      dismissible: options.dismissible !== false,
      ...(options.action && { action: options.action }),
      ...(options.cancel && { cancel: options.cancel }),
    });
  }

  error(message: string, options: ToastOptions = {}) {
    return sonnerToast.error(message, {
      duration: options.duration || 6000,
      position: options.position,
      dismissible: options.dismissible !== false,
      ...(options.action && { action: options.action }),
      ...(options.cancel && { cancel: options.cancel }),
    });
  }

  warning(message: string, options: ToastOptions = {}) {
    return sonnerToast.warning(message, {
      duration: options.duration || 5000,
      position: options.position,
      dismissible: options.dismissible !== false,
      ...(options.action && { action: options.action }),
      ...(options.cancel && { cancel: options.cancel }),
    });
  }

  info(message: string, options: ToastOptions = {}) {
    return sonnerToast.info(message, {
      duration: options.duration || 4000,
      position: options.position,
      dismissible: options.dismissible !== false,
      ...(options.action && { action: options.action }),
      ...(options.cancel && { cancel: options.cancel }),
    });
  }

  loading(message: string, options: Omit<ToastOptions, 'duration'> = {}) {
    return sonnerToast.loading(message, {
      position: options.position,
      dismissible: options.dismissible !== false,
      ...(options.action && { action: options.action }),
      ...(options.cancel && { cancel: options.cancel }),
    });
  }

  // 自定义通知
  custom(content: React.ReactNode | ((id: string | number) => React.ReactElement), options: ToastOptions = {}) {
    // sonner的custom方法接受ReactNode或函数
    return sonnerToast.custom(typeof content === 'function' ? content : () => content as React.ReactElement, {
      duration: options.duration || 4000,
      position: options.position,
      dismissible: options.dismissible !== false,
    });
  }

  // Promise 通知 - 自动处理加载、成功、错误状态
  promise<T>(
    promise: Promise<T>,
    options: LoadingToastOptions & {
      loading?: string;
      success?: string | ((data: T) => string);
      error?: string | ((error: unknown) => string);
    }
  ) {
    return sonnerToast.promise(promise, {
      loading: options.loading || options.loadingText || '处理中...',
      success: (data) => {
        if (typeof options.success === 'function') {
          return options.success(data);
        }
        return options.success || options.successText || '操作成功';
      },
      error: (error) => {
        if (typeof options.error === 'function') {
          return options.error(error);
        }
        return options.error || options.errorText || '操作失败';
      },
      position: options.position,
      dismissible: options.dismissible !== false,
      ...(options.action && { action: options.action }),
      ...(options.cancel && { cancel: options.cancel }),
    });
  }

  // 操作确认通知
  confirm(
    message: string,
    onConfirm: () => void | Promise<void>,
    options: ToastOptions & {
      confirmText?: string;
      cancelText?: string;
    } = {}
  ) {
    const {
      confirmText = '确认',
      cancelText = '取消',
      ...toastOptions
    } = options;

    return this.warning(message, {
      ...toastOptions,
      duration: 0, // 不自动消失
      action: {
        label: confirmText,
        onClick: async () => {
          try {
            await onConfirm();
          } catch (error: unknown) {
            const err = error as { message?: string };
            this.error(err?.message || '操作失败');
          }
        },
      },
      cancel: {
        label: cancelText,
        onClick: () => {
          // 取消操作，toast会自动消失
        },
      },
    });
  }

  // 网络错误通知
  networkError(error?: unknown, options: ToastOptions = {}) {
    let message = '网络连接失败';
    
    const err = error as { response?: { status?: number }; message?: string; code?: string } | undefined;
    if (err?.response?.status) {
      switch (err.response.status) {
        case 400:
          message = '请求参数错误';
          break;
        case 401:
          message = '未授权，请重新登录';
          break;
        case 403:
          message = '权限不足';
          break;
        case 404:
          message = '请求的资源不存在';
          break;
        case 429:
          message = '请求过于频繁，请稍后再试';
          break;
        case 500:
          message = '服务器内部错误';
          break;
        case 502:
          message = '网关错误';
          break;
        case 503:
          message = '服务暂时不可用';
          break;
        default:
          message = `网络错误 (${err.response.status})`;
      }
    } else if (err && 'code' in err && err.code === 'NETWORK_ERROR') {
      message = '网络连接失败，请检查网络设置';
    }

    return this.error(message, options);
  }

  // 表单验证错误通知
  validationError(errors: Record<string, string[]> | string, options: ToastOptions = {}) {
    if (typeof errors === 'string') {
      return this.error(errors, options);
    }

    const errorMessages = Object.entries(errors)
      .map(([field, messages]) => `${field}: ${messages.join(', ')}`)
      .join('\n');

    return this.error(`表单验证失败:\n${errorMessages}`, {
      ...options,
      duration: 6000,
    });
  }

  // 批量操作结果通知
  batchResult(
    results: { success: number; failed: number; total: number },
    options: ToastOptions & {
      successText?: string;
      partialText?: string;
      failedText?: string;
    } = {}
  ) {
    const { success, failed, total } = results;
    const {
      successText = '操作完成',
      partialText = '部分操作失败',
      failedText = '操作失败',
      ...toastOptions
    } = options;

    if (failed === 0) {
      return this.success(`${successText} (${success}/${total})`, toastOptions);
    } else if (success > 0) {
      return this.warning(`${partialText} (成功: ${success}, 失败: ${failed})`, toastOptions);
    } else {
      return this.error(`${failedText} (${failed}/${total})`, toastOptions);
    }
  }

  // 关闭所有通知
  dismiss(toastId?: string | number) {
    return sonnerToast.dismiss(toastId);
  }

  // 关闭所有通知
  dismissAll() {
    return sonnerToast.dismiss();
  }
}

// 导出单例实例
export const toast = new ToastManager();